/**
 * Reset-password page. Reads ?token= from the emailed link, collects a new
 * password, POSTs to /api/auth/reset-password, then bounces to /login (the API
 * deliberately does NOT auto-sign-in — the user proves the new password).
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, CheckCircle2, KeyRound, Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const missingToken = !token;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.resetPassword(token, newPassword);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not reset password.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-2 p-4">
      <div className="card w-full max-w-sm p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <BookOpen size={22} className="text-accent" />
          <span className="text-lg font-semibold">STR Wiki</span>
        </div>

        {done ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded bg-surface-3 p-3 text-sm text-ink-muted">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-accent" />
              <p>Your password has been reset. You can now sign in with it.</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="btn btn-primary w-full"
            >
              Go to sign in
            </button>
          </div>
        ) : missingToken ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-muted">
              This reset link is missing its token. Please request a new one.
            </p>
            <Link to="/forgot" className="btn btn-primary w-full">
              Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="flex items-start gap-2 rounded bg-surface-3 p-3 text-sm text-ink-muted">
              <KeyRound size={16} className="mt-0.5 flex-shrink-0" />
              <p>Choose a new password for your account.</p>
            </div>
            <div>
              <label htmlFor="newPassword" className="mb-1 block text-xs font-medium text-ink-muted">
                New password
              </label>
              <input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                required
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="mb-1 block text-xs font-medium text-ink-muted">
                Confirm new password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="Re-enter new password"
              />
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <button type="submit" disabled={busy} className="btn btn-primary w-full">
              {busy && <Loader2 size={15} className="animate-spin" />}
              Reset password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
